import React, { useEffect, useState } from 'react';
import { useProfile } from '../Context/ProfileContext';
import { Link } from 'react-router-dom';
import { getData, putData, API_BASE_URL } from '../Services/api';
import { toast } from 'react-toastify';
import Pagination from '../Services/Pagination';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const formatTimeAMPM = (timeStr) => {
    if (!timeStr) return '';
    const str = String(timeStr).trim();
    if (/am|pm/i.test(str)) return str;
    const parts = str.split(':');
    if (parts.length >= 2) {
        let hours = parseInt(parts[0], 10);
        const minutes = parts[1].substring(0, 2).padStart(2, '0');
        if (!isNaN(hours)) {
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            const hoursStr = String(hours).padStart(2, '0');
            return `${hoursStr}:${minutes} ${ampm}`;
        }
    }
    return str;
};

const ViewEditPO = () => {
    const { profile } = useProfile();
    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage] = useState(50);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [poTypeFilter, setPoTypeFilter] = useState('all');
    const [poTypesList, setPoTypesList] = useState([]);

    const fetchOrders = async (pageNumber = page, searchQuery = search, statusQ = statusFilter, poTypeQ = poTypeFilter) => {
        try {
            const res = await getData(`/proforma-orders/all?page=${pageNumber}&limit=${perPage}&search=${searchQuery}&status=${statusQ}&po_type=${poTypeQ}`);
            if (res.status) {
                setOrders(res.data || []);
                setPage(res.pagination.page || 1);
                setPages(res.pagination.totalPages || 1);
                setTotal(res.pagination.total || 0);
            }
        } catch (error) {
            console.error("Error fetching POs:", error);
        }
    };

    useEffect(() => {
        const fetchPoTypes = async () => {
            try {
                const res = await getData('/po-types');
                if (res.status) {
                    setPoTypesList(res.data || []);
                }
            } catch (e) {
                console.error("Failed to fetch PO types", e);
            }
        };
        fetchPoTypes();
    }, []);

    useEffect(() => {
        fetchOrders();
        const intervalId = setInterval(() => {
            fetchOrders();
        }, 15000);
        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, search, statusFilter, poTypeFilter]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber !== page) {
            setPage(pageNumber);
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            const res = await putData(`/proforma-orders/status/${orderId}`, { status: newStatus });
            if (res.status) {
                toast.success("Status updated successfully!");
                fetchOrders(); // refresh the list
            } else {
                toast.error(res.message || "Failed to update status");
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error(error.response?.data?.message || "Failed to update status");
        }
    };



    const generateInvoicePDF = (order) => {
        try {
            // Orientation: landscape, A4 dimensions: 297mm x 210mm
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

            const width = 133;
            // Swapped Code and Product Name, minimized Code to 12 and gave the rest to Product Name (51)
            const colWidths = [10, 12, 51, 12, 12, 16, 20];
            const colAlignments = ["center", "center", "left", "center", "center", "right", "right"];

            let products = [];
            try {
                products = typeof order.products === 'string' ? JSON.parse(order.products) : (order.products || []);
            } catch (e) {
                console.error("Failed to parse products", e);
            }

            const drawRow = (startX, y, rowData, bgFillColor) => {
                if (bgFillColor) {
                    doc.setFillColor(bgFillColor[0], bgFillColor[1], bgFillColor[2]);
                    doc.rect(startX, y, width, 8, 'F');
                }
                doc.rect(startX, y, width, 8); // outer border

                let currentX = startX;
                for (let i = 0; i < colWidths.length; i++) {
                    const w = colWidths[i];
                    const text = String(rowData[i] !== undefined && rowData[i] !== null ? rowData[i] : '');
                    const align = colAlignments[i];

                    if (i > 0) {
                        doc.line(currentX, y, currentX, y + 8);
                    }

                    let textX = currentX + 2;
                    if (align === "center") {
                        textX = currentX + w / 2;
                    } else if (align === "right") {
                        textX = currentX + w - 2;
                    }

                    doc.setFontSize(10); // Increase Font Size of Product List to 10

                    if (i === 2 && text !== "Product Name") { // Product Name column text wrapping
                        const lines = doc.splitTextToSize(text, w - 3);
                        if (lines.length > 1) {
                            doc.text(lines, textX, y + 3.5, { align: align });
                        } else {
                            doc.text(text, textX, y + 5.5, { align: align });
                        }
                    } else {
                        doc.text(text, textX, y + 5.5, { align: align });
                    }
                    currentX += w;
                }
            };

            const drawTotalRow = (startX, y, totalAmt) => {
                doc.rect(startX, y, width, 8);
                doc.line(startX + 85, y, startX + 85, y + 8);
                doc.line(startX + 113, y, startX + 113, y + 8);

                doc.setTextColor(0, 0, 0);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.text("Total", startX + 85 + 14, y + 5.5, { align: "center" });

                doc.setTextColor(255, 0, 0);
                doc.text(Number(totalAmt).toLocaleString(), startX + 113 + 18, y + 5.5, { align: "right" });
            };

            const companyName = order.resolved_company_name || order.createdByUser?.company_name || profile?.company_name || 'Company Name';

            const drawInvoiceSection = (startX, isDuplicate) => {
                // 1. Company Name Row (y = 10 to 18)
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.2);
                doc.rect(startX, 10, width, 8);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.setTextColor(255, 0, 0);
                doc.text(companyName, startX + width / 2, 16, { align: "center" });

                // 2. Type Row (y = 18 to 26) with different color coding for each PO type
                doc.rect(startX, 18, 10, 8);
                doc.setFontSize(9);
                doc.setTextColor(0, 0, 0);
                doc.setFont("helvetica", "bold");
                doc.text("Type", startX + 5, 23.5, { align: "center" });

                let typeBgColor = [255, 255, 0]; // Default Yellow
                let typeTextColor = [255, 0, 0]; // Default Red
                if (order.po_type_color && order.po_type_color.toLowerCase() !== '#ffffff') {
                    const hexToRgb = (hex) => {
                        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                        return result ? [
                            parseInt(result[1], 16),
                            parseInt(result[2], 16),
                            parseInt(result[3], 16)
                        ] : [255, 255, 255];
                    };
                    const getContrastColor = (rgb) => {
                        const yiq = ((rgb[0] * 299) + (rgb[1] * 587) + (rgb[2] * 114)) / 1000;
                        return (yiq >= 128) ? [0, 0, 0] : [255, 255, 255];
                    };
                    typeBgColor = hexToRgb(order.po_type_color);
                    typeTextColor = getContrastColor(typeBgColor);
                } else if (order.po_type) {
                    const t = String(order.po_type).toUpperCase();
                    if (t.includes("DOMESTIC")) {
                        typeBgColor = [220, 245, 220]; // Light Green
                        typeTextColor = [0, 128, 0];
                    } else if (t.includes("EXPORT")) {
                        typeBgColor = [220, 235, 255]; // Light Blue
                        typeTextColor = [0, 0, 180];
                    } else if (t.includes("LOCAL")) {
                        typeBgColor = [255, 235, 205]; // Light Orange
                        typeTextColor = [160, 82, 45];
                    } else if (t.includes("SPECIAL")) {
                        typeBgColor = [240, 220, 255]; // Light Violet
                        typeTextColor = [128, 0, 128];
                    }
                }

                doc.setFillColor(typeBgColor[0], typeBgColor[1], typeBgColor[2]);
                doc.rect(startX + 10, 18, 123, 8, 'FD');
                doc.setFontSize(10);
                doc.setTextColor(typeTextColor[0], typeTextColor[1], typeTextColor[2]);
                doc.text(order.po_type || '-', startX + 10 + 123 / 2, 23.5, { align: "center" });

                // 3. Delivery Note Row (y = 26 to 34)
                doc.rect(startX, 26, width, 8);
                doc.setFontSize(11);
                doc.setTextColor(0, 0, 0);
                const titleText = isDuplicate ? "Delivery Note - Duplicate" : "Delivery Note";
                doc.text(titleText, startX + width / 2, 31.5, { align: "center" });

                // Compute invoice creation date & time (never PO date/time)
                let invoiceDateStr = '-';
                if (order.invoice_date) {
                    invoiceDateStr = order.invoice_date;
                    if (order.invoice_time) {
                        invoiceDateStr += ` ${order.invoice_time}`;
                    }
                } else if (order.invoice_number || order.status === 'invoiced') {
                    if (order.updatedAt) {
                        const uDate = new Date(order.updatedAt);
                        const yyyy = uDate.getFullYear();
                        const mm = String(uDate.getMonth() + 1).padStart(2, '0');
                        const dd = String(uDate.getDate()).padStart(2, '0');
                        let hours = uDate.getHours();
                        const minutes = String(uDate.getMinutes()).padStart(2, '0');
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        hours = hours % 12;
                        hours = hours ? hours : 12;
                        invoiceDateStr = `${yyyy}-${mm}-${dd} ${hours}:${minutes} ${ampm}`;
                    }
                }

                // 4. Invoice Number & Date & Time Row (y = 34 to 42)
                doc.rect(startX, 34, width, 8);
                doc.line(startX + 28, 34, startX + 28, 42);
                doc.line(startX + 65, 34, startX + 65, 42);
                doc.line(startX + 88, 34, startX + 88, 42);

                doc.setFontSize(8.5);
                doc.setTextColor(255, 0, 0);
                doc.text("Invoice Number", startX + 2, 39.5);
                doc.setTextColor(0, 0, 0);
                doc.text(order.invoice_number || 'PENDING', startX + 30, 39.5);

                doc.setTextColor(255, 0, 0);
                doc.text("Date & Time", startX + 67, 39.5);
                doc.setTextColor(0, 0, 0);
                doc.text(invoiceDateStr, startX + 90, 39.5);

                // 5. Customer Name Row (y = 42 to 50)
                doc.rect(startX, 42, width, 8);
                doc.line(startX + 30, 42, startX + 30, 50);

                doc.setTextColor(255, 0, 0);
                doc.text("Customer Name", startX + 2, 47.5);
                doc.setTextColor(0, 0, 0);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12); // Increase the customer’s name font size upto 12
                doc.text(order.customer_name, startX + 32, 47.5);

                // 6. Header Row (y = 50 to 58) - Code is now before Product Name
                const headerData = ["Sr No", "Code", "Product Name", "Qty", "Units", "Rate", "Amount"];
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                drawRow(startX, 50, headerData, [189, 215, 238]);

                // 7. Products Table (y = 58 onwards)
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                const totalRows = Math.max(products.length, 10);
                let currentY = 58;
                for (let i = 0; i < totalRows; i++) {
                    const prod = products[i] || {};
                    const srNo = i + 1;
                    const name = prod.product || prod.name || '';
                    const code = prod.code || '';
                    const qty = prod.qty || prod.quantity || '';
                    const unit = prod.unit || '';
                    const rate = prod.rate || prod.price || '';
                    const amt = prod.amount || (prod.qty * prod.rate) || '';

                    // Order: Sr No, Code, Name, Qty, Unit, Rate, Amount
                    const rowData = [srNo, code, name, qty, unit, rate, amt ? Number(amt).toLocaleString() : ''];
                    drawRow(startX, currentY, rowData, null);
                    currentY += 8;
                }

                // 8. Total Row
                drawTotalRow(startX, currentY, order.total_amount);

                // 9. Comments Box at bottom if present
                if (order.comments) {
                    const commentY = currentY + 12;
                    doc.setFont("helvetica", "normal");
                    doc.setFontSize(9);
                    doc.setTextColor(0, 0, 0);
                    doc.text("Comment: " + order.comments, startX, commentY, { maxWidth: width });
                }

                // Add CANCELLED Watermark if order is cancelled
                if (order.status === 'cancelled') {
                    doc.saveGraphicsState();
                    let hasOpacity = false;
                    try {
                        if (doc.GState) {
                            doc.setGState(new doc.GState({ opacity: 0.15 }));
                            hasOpacity = true;
                        }
                    } catch (e) {
                        console.warn("jsPDF GState error:", e);
                    }
                    if (hasOpacity) {
                        doc.setTextColor(220, 53, 69);
                    } else {
                        doc.setTextColor(255, 200, 200);
                    }
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(45);
                    doc.text("CANCELLED", startX + width / 2, 85, { align: "center", angle: 30 });
                    doc.restoreGraphicsState();
                }
            };

            // First mention "Delivery Note - Duplicate" copy page and then 2nd page "Delivery Note"
            // Left page is duplicate
            drawInvoiceSection(10, true);

            // Draw dashed vertical separator line in the middle
            doc.setDrawColor(150, 150, 150);
            doc.setLineDashPattern([2, 2], 0);
            doc.line(148.5, 10, 148.5, 160);
            doc.setLineDashPattern([], 0);

            // Right page is original
            drawInvoiceSection(154, false);

            // Save the PDF
            doc.save(`Invoice_${order.invoice_number || order.po_number}.pdf`);
            toast.success("PDF Downloaded successfully!");
        } catch (err) {
            console.error("PDF Gen Error:", err);
            toast.error("Failed to generate PDF. Check console for details.");
        }
    };

    const handleExportExcel = async (orderId, invoiceNumber, poNumber) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}proforma-orders/export-excel/${orderId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!res.ok) throw new Error("Failed to export Excel");

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Invoice_${invoiceNumber || poNumber}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success("Excel Downloaded successfully!");
        } catch (error) {
            console.error("Excel Export Error:", error);
            toast.error("Failed to export Excel file.");
        }
    };

    return (
        <div className="pc-container">
            <div className="pc-content">
                <div className="page-header">
                    <div className="page-block">
                        <div className="row align-items-center">
                            <div className="col-md-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/accounts-dashboard/view-edit">Accounts</Link></li>
                                    <li className="breadcrumb-item" aria-current="page">View & Edit PO</li>
                                </ul>
                            </div>
                            <div className="col-md-12">
                                <div className="page-header-title">
                                    <h2 className="mb-0">View & Generate Invoices</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12">
                        <div className="card table-card">
                            <div className="card-body">
                                <div className="d-sm-flex align-items-center p-4">
                                    <ul className="list-inline me-auto my-1">
                                        <li className="list-inline-item">
                                            <div className="form-search">
                                                <i className="fas fa-search" />
                                                <input
                                                    type="search"
                                                    className="form-control"
                                                    placeholder="Search Customer or PO Number"
                                                    value={search}
                                                    onChange={(e) => {
                                                        setSearch(e.target.value);
                                                        setPage(1);
                                                    }}
                                                />
                                            </div>
                                        </li>
                                        <li className="list-inline-item ms-2">
                                            <select
                                                className="form-select"
                                                value={statusFilter}
                                                onChange={(e) => {
                                                    setStatusFilter(e.target.value);
                                                    setPage(1);
                                                }}
                                            >
                                                <option value="all">All Status</option>
                                                <option value="checking">Checking</option>
                                                <option value="cancelled">Cancelled</option>
                                                <option value="invoiced">Invoiced</option>
                                            </select>
                                        </li>
                                        <li className="list-inline-item ms-2">
                                            <select
                                                className="form-select"
                                                value={poTypeFilter}
                                                onChange={(e) => {
                                                    setPoTypeFilter(e.target.value);
                                                    setPage(1);
                                                }}
                                            >
                                                <option value="all">All PO Types</option>
                                                {poTypesList.map(type => (
                                                    <option key={type.id} value={type.type_name}>{type.type_name}</option>
                                                ))}
                                            </select>
                                        </li>
                                    </ul>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-hover" id="pc-dt-simple">
                                        <thead>
                                            <tr>
                                                <th>PO Number</th>
                                                <th>PO Type</th>
                                                <th>Customer Name</th>
                                                <th>Date & Time</th>
                                                <th>Total Amount</th>
                                                <th>Status</th>
                                                <th className="text-center">View & Edit</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orders.length > 0 ? orders.map((order, index) => (
                                                <tr key={order.id}>
                                                    <td className="fw-bold text-dark">{order.po_number}</td>
                                                    <td>{order.po_type || '-'}</td>
                                                    <td className="fw-bold text-dark">{order.customer_name}</td>
                                                    <td>
                                                        <div>{order.date}</div>
                                                        <div className="text-muted small">{formatTimeAMPM(order.time)}</div>
                                                    </td>
                                                    <td className="fw-bold text-dark">Tzs {Number(order.total_amount).toLocaleString('en-US')}</td>
                                                    <td>
                                                        {order.status === 'invoiced' ? (
                                                            <>
                                                                <span className="badge bg-success">INVOICED</span>
                                                                {order.invoice_number && (
                                                                    <div className="mt-1 small fw-bold text-info">{order.invoice_number}</div>
                                                                )}
                                                            </>
                                                        ) : order.status === 'cancelled' ? (
                                                            <>
                                                                <span className="badge bg-danger">CANCELLED</span>
                                                                {order.invoice_number && (
                                                                    <div className="mt-1 small fw-bold text-info">{order.invoice_number}</div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={order.status}
                                                                onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                                                style={{ minWidth: '130px' }}
                                                            >
                                                                <option value="checking">Checking</option>
                                                                <option value="cancelled">Cancelled</option>
                                                            </select>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                                            <Link
                                                                to={`/accounts-dashboard/view-po/${order.id}`}
                                                                className="avtar avtar-xs btn-pc-default btn-link-secondary"
                                                                title="View PO Details"
                                                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}
                                                            >
                                                                <i className="fas fa-eye f-18" />
                                                            </Link>
                                                            {order.status !== 'invoiced' && order.status !== 'cancelled' && (
                                                                <Link
                                                                    to={`/accounts-dashboard/edit-po/${order.id}`}
                                                                    className="avtar avtar-xs btn-pc-default btn-link-success"
                                                                    title="Edit PO"
                                                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}
                                                                >
                                                                    <i className="fas fa-edit f-18" />
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                                            {order.status === 'invoiced' || (order.status === 'cancelled' && order.invoice_number) ? (
                                                                <>
                                                                    <button
                                                                        className="btn btn-sm btn-danger text-white px-3"
                                                                        title="Download PDF"
                                                                        onClick={() => generateInvoicePDF(order)}
                                                                        style={{ margin: 0 }}
                                                                    >
                                                                        <i className="fas fa-download me-1" /> PDF
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-warning px-3"
                                                                        title="Export Excel"
                                                                        onClick={() => handleExportExcel(order.id, order.invoice_number, order.po_number)}
                                                                        style={{ margin: 0 }}
                                                                    >
                                                                        <i className="fas fa-file-excel me-1" /> Excel
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <Link
                                                                    to={`/accounts-dashboard/view-po/${order.id}`}
                                                                    className="btn btn-sm btn-primary px-3"
                                                                    title="Preview & Generate"
                                                                    style={{ margin: 0 }}
                                                                >
                                                                    <i className="fas fa-file-invoice me-1" /> Generate
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td className='text-center' colSpan={8}><strong>No Proforma Orders Found.</strong></td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="d-flex justify-content-between align-items-center px-4 pb-3">
                                    <div className="text-muted">
                                        Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total} entries
                                    </div>
                                    <Pagination page={page} pages={pages} onPageChange={handlePageChange} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewEditPO;
